import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, RequestFailed } from '../api/client';
import { PasswordField } from './PasswordField';

/**
 * Смена пароля по одноразовой ссылке.
 *
 * Открывается без входа: человек как раз войти и не может. Ссылку выдаёт
 * администратор, а владельцу её печатает сервер при запуске с особой
 * переменной окружения.
 */
export function ResetPasswordScreen() {
  const { code = '' } = useParams();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [invalidReason, setInvalidReason] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const mismatch = passwordAgain.length > 0
    && passwordAgain.length >= password.length
    && passwordAgain !== password;

  // Проверяем ссылку до показа формы: заполнять её впустую обидно
  useEffect(() => {
    let cancelled = false;
    api.checkPasswordReset(code)
      .then((result) => {
        if (cancelled) return;
        setInvalidReason(result.valid ? null : result.reason ?? 'Ссылка недействительна');
        setDisplayName(result.displayName);
      })
      .catch(() => !cancelled && setInvalidReason('Не удалось проверить ссылку'))
      .finally(() => !cancelled && setChecking(false));
    return () => { cancelled = true; };
  }, [code]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (password !== passwordAgain) {
      setError('Пароли не совпадают — проверьте оба поля');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.resetPassword(code, password);
      setDone(true);
    } catch (e) {
      setError(e instanceof RequestFailed ? e.error.message : 'Не удалось соединиться с сервером');
    } finally {
      setBusy(false);
    }
  }

  if (checking) {
    return <div className="centered"><p className="sub">Проверяем ссылку…</p></div>;
  }

  if (invalidReason) {
    return (
      <div className="centered">
        <div className="card">
          <h1>Ссылка не подходит</h1>
          <p className="sub">{invalidReason}</p>
          <p className="sub">
            Попросите администратора выдать новую — прежняя могла быть уже
            использована или просрочена.
          </p>
          <button onClick={() => navigate('/', { replace: true })}>На страницу входа</button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="centered">
        <div className="card">
          <h1>Пароль изменён</h1>
          {/* Про отзыв сессий говорим прямо: человек увидит, что его выкинуло
              на других устройствах, и не должен считать это поломкой */}
          <p className="sub">
            Вход выполнен заново на всех устройствах — это защита на случай,
            если доступом успел воспользоваться кто-то ещё.
          </p>
          <button onClick={() => navigate('/', { replace: true })}>Войти</button>
        </div>
      </div>
    );
  }

  return (
    <div className="centered">
      <form className="card" onSubmit={submit}>
        <h1>Новый пароль</h1>
        <p className="sub">
          {displayName
            ? `Меняем пароль для: ${displayName}`
            : 'Придумайте новый пароль'}
        </p>

        <PasswordField
          id="newPassword"
          label="Новый пароль"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={8}
          placeholder="не короче 8 символов"
        />

        <PasswordField
          id="newPasswordAgain"
          label="Повторите пароль"
          value={passwordAgain}
          onChange={setPasswordAgain}
          autoComplete="new-password"
          minLength={8}
          error={mismatch ? 'Пароли не совпадают' : null}
        />

        <button type="submit" disabled={busy || mismatch || !password}>
          {busy ? 'Меняем…' : 'Сменить пароль'}
        </button>

        {error && <p className="error">{error}</p>}
      </form>
    </div>
  );
}
